import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AdminSidebar } from './AdminSidebar';
import { AdminHeader } from './AdminHeader';
import { useAutoLogout } from '@/hooks/useAutoLogout';
import { resolveMenuPath, useMyPermissions } from '@/hooks/usePermissions';
import { useAuthMe } from '@/hooks/useAuthMe';
import { normalizeQueryString } from '@/utils/urlQuery';
import { canUseTkTune } from '@/lib/ui/tkTuneAccess';
import { setGateSnapshot, setOverlayVisible } from '@/lib/bootDebugStore';
import { beginTransition } from '@/lib/loading/loadingCoordinatorStore';
import { useLoadingRequest } from '@/hooks/useLoadingRequest';
import { BootDebugOverlay } from '@/components/dev/BootDebugOverlay';
import { traceLoading } from '@/lib/traceLoading';
import { LoadingTraceOverlay } from '@/components/dev/LoadingTraceOverlay';
import { getHasSettledOnce, setHasSettledOnce, resetHasSettledOnce } from '@/lib/bootGateStore';
import { AuthSessionCoordinatorBridge } from '@/components/auth/AuthSessionCoordinatorBridge';
import { incrementCompatMetric } from '@/lib/compatMetrics';
import { RouteChunkLoader } from '@/components/ui/route-chunk-loader';
import { AppearanceDraftProvider } from '@/contexts/AppearanceDraftContext';
import { AppearanceUnsavedNavigationGuard } from '@/components/settings/AppearanceUnsavedChanges';

export const AdminLayout = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { permissionsByPath, isAdmin, isLoading: permissionsLoading, isError: permissionsError, isSuccess: permissionsSuccess } = useMyPermissions();

  const { data: currentUser, isLoading: authMeLoading, status: authStatus } = useAuthMe();

  // Trace gate flags (DEV) for flip/flop diagnosis
  if (import.meta.env.DEV) {
    traceLoading('permissionsLoading', permissionsLoading, { pathname: location.pathname });
    traceLoading('authMeLoading', authMeLoading, { pathname: location.pathname });
    traceLoading('hasCurrentUser', !!currentUser, { pathname: location.pathname });
  }

  const tkTuneDeniedOnceRef = useRef<string | null>(null);

  /**
   * Query normalization + tkTune permission gate.
   *
   * Requirements:
   * - Query parsing must be order-independent (URLSearchParams).
   * - If query is malformed (multiple "?"), sanitize and replace URL.
   * - tkTune must NOT be enabled for regular users:
   *   - If user tries "?tkTune=1" but lacks permission, auto-remove param (replace)
   *   - Show a "Không có quyền" message (toast) at most once per route
   */
  useEffect(() => {
    const normalized = normalizeQueryString(location.search);
    const params = new URLSearchParams(normalized);

    const requestedTkTune = params.get('tkTune') === '1';

    // Only enforce tkTune permission once we can reliably decide.
    const canEnforceTkTune = permissionsSuccess && !permissionsLoading && (isAdmin || !!currentUser?.role);
    const allowedTkTune = canUseTkTune({ isAdmin, roleName: currentUser?.role });

    let nextSearch = normalized;
    let removedTkTune = false;

    if (requestedTkTune && canEnforceTkTune && !allowedTkTune) {
      params.delete('tkTune');
      removedTkTune = true;
      nextSearch = params.toString() ? `?${params.toString()}` : '';
    }

    if (nextSearch !== location.search) {
      navigate({ pathname: location.pathname, search: nextSearch, hash: location.hash }, { replace: true });
    }

    if (removedTkTune) {
      const routeKey = location.pathname;
      if (tkTuneDeniedOnceRef.current !== routeKey) {
        // Inline: no toast; column tune disabled when no permission
        tkTuneDeniedOnceRef.current = routeKey;
      }
    }
  }, [
    currentUser?.role,
    isAdmin,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    permissionsLoading,
    permissionsSuccess,
  ]);

  // Auto logout hook
  useAutoLogout();

  // Auth guard (requires token from /api/auth/login)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Preserve current location (pathname + search + hash) as returnUrl
      // Only redirect if not already on login page to avoid loops
      if (location.pathname !== '/login') {
        const returnUrl = encodeURIComponent(location.pathname + location.search + location.hash);
        navigate(`/login?returnUrl=${returnUrl}`, { replace: true });
      }
    }
  }, [navigate, location]);

  // Helper to find first accessible menu path
  const getFirstAccessiblePath = (): string | null => {
    // Priority order for fallback navigation
    const priorityPaths = [
      '/overview',
      '/',
      '/work-reports',
      '/incidents',
      '/optimization/sizing', // S7.3 canonical (legacy /optimization redirects here)
      '/incidents-config',  // Incidents Config
      '/projects/list',
      '/projects/assignment',
      '/projects/duty-schedules',
      '/permissions/accounts',
      '/permissions/roles',
      '/config/overview',  // New path (replaces /settings)
      '/settings',  // Keep for backward compatibility (will redirect)
    ];

    for (const path of priorityPaths) {
      const perm = permissionsByPath.get(path);
      if (perm?.can_view) {
        return path;
      }
    }
    return null;
  };

  // Route permission guard (menu can_view)
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  
  useEffect(() => {
    // CRITICAL: Wait for permissions to load before checking access
    // This prevents race condition where guard redirects before data is available
    if (permissionsLoading || !permissionsSuccess) {
      setHasAccess(null); // Keep loading state, don't redirect
      return;
    }

    // If permissions failed to load, don't block (let API handle auth redirect)
    if (permissionsError) {
      setHasAccess(null);
      return;
    }

    const resolved = resolveMenuPath(location.pathname);
    
    // Always allow profile pages
    if (resolved === '/profile' || location.pathname.startsWith('/profile')) {
      setHasAccess(true);
      return;
    }

    // S7.3: exact legacy `/optimization` is a migration alias only.
    // Allow Outlet so <Navigate replace> can run; the target `/optimization/sizing`
    // route is gated normally. Do NOT widen other /optimization/* paths.
    if (location.pathname === '/optimization' || location.pathname === '/optimization/') {
      setHasAccess(true);
      return;
    }

    const perm = permissionsByPath.get(resolved);
    
    // Block if: no permission entry found OR can_view is false
    // Only redirect if permissions data has successfully loaded (permissionsSuccess = true)
    // This prevents false negative when permissions are still loading
    if (!perm || !perm.can_view) {
      setHasAccess(false);
      // Try to redirect to first accessible path
      const fallbackPath = getFirstAccessiblePath();
      if (fallbackPath && fallbackPath !== resolved) {
        navigate(fallbackPath, { replace: true });
      }
    } else {
      setHasAccess(true);
    }
  }, [location.pathname, navigate, permissionsByPath, permissionsLoading, permissionsError, permissionsSuccess]);

  // Full-page loading: only while permissions or auth/me are loading (no cached user yet)
  // Gate does NOT depend on column settings. Once settled, do NOT flip back to full-screen loading (fix flip/flop).
  const showInitialLoader = permissionsLoading || (authMeLoading && !currentUser);

  /** Coordinator owns gate loading via useLoadingRequest below — no bootstrapping flip-flop. */
  const showAccessLoadingSkeleton = hasAccess === null;

  /** 01A: Outlet only after RBAC + auth/admin gate settled (P2 overlay owns fullscreen gate until then). */
  const isAdminGateSettled =
    !showInitialLoader && authStatus === 'authenticated' && !!currentUser;
  const canRenderOutlet = hasAccess === true && isAdminGateSettled;

  const showGateLoader = showAccessLoadingSkeleton || (hasAccess === true && !canRenderOutlet);
  useLoadingRequest('admin-gate', showGateLoader, {
    routePath: location.pathname,
    idSuffix: 'layout-gate',
  });

  if (import.meta.env.DEV) traceLoading('showInitialLoader', showInitialLoader, { permissionsLoading, authMeLoading, hasCurrentUser: !!currentUser, pathname: location.pathname });

  const gateRenderCountRef = useRef(0);
  const gateFlagsRef = useRef({ permissionsLoading, authMeLoading, permissionsSuccess, hasCurrentUser: !!currentUser, hasAccess });
  if (showInitialLoader) {
    gateRenderCountRef.current += 1;
    gateFlagsRef.current = { permissionsLoading, authMeLoading, permissionsSuccess, hasCurrentUser: !!currentUser, hasAccess };
  }

  const [showLoadErrorFallback, setShowLoadErrorFallback] = useState(false);
  const SAFETY_LOAD_TIMEOUT_MS = 8_000;

  // Reset "settled" when no token or on login page so next session can show loading again (module-level survives remount)
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  if (!token || location.pathname === '/login') {
    resetHasSettledOnce();
  }

  useEffect(() => {
    if (!showInitialLoader) {
      setHasSettledOnce(true);
      setIsLoading(false);
      setShowLoadErrorFallback(false);
      if (import.meta.env.DEV) {
        traceLoading('gate_setLoading', false, { reason: 'showInitialLoader_false', pathname: location.pathname });
        setOverlayVisible(false);
      }
      return;
    }
    // Stickiness: do not show full-screen loading again after we've settled once (fix flip/flop; module-level survives route remount)
    if (getHasSettledOnce()) {
      if (import.meta.env.DEV) traceLoading('gate_skipLoadingTrue', true, { reason: 'hasSettled_sticky', pathname: location.pathname });
      return;
    }
    setIsLoading(true);
    setShowLoadErrorFallback(false);
    if (import.meta.env.DEV) traceLoading('gate_setLoading', true, { reason: 'showInitialLoader_true', pathname: location.pathname });
    const timeoutId = window.setTimeout(() => {
      setHasSettledOnce(true);
      setIsLoading(false);
      setShowLoadErrorFallback(true);
      if (import.meta.env.DEV) {
        traceLoading('gate_setLoading', false, { reason: 'safety_timeout_8s', pathname: location.pathname });
        console.warn('[AdminLayout] Safety: load timeout 8s – showing reload fallback');
        setOverlayVisible(false);
      }
    }, SAFETY_LOAD_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [showInitialLoader, location.pathname]);

  if (import.meta.env.DEV) traceLoading('isLoading', isLoading, { pathname: location.pathname });

  // DEV-only: Loading Gate Inspector – 1st snapshot at 1500ms, 2nd at 6s (pathname, flags, render count, timestamp)
  const gateSnapshotNavKeyRef = useRef<string | null>(null);
  const gateFirstSnapshotDoneRef = useRef(false);
  const LOADING_OVERLAY_THRESHOLD_MS = 1500;
  const LOADING_SECOND_SNAPSHOT_MS = 6000;
  const navStartRef = useRef(0);

  useEffect(() => {
    if (!showInitialLoader) {
      gateSnapshotNavKeyRef.current = null;
      gateFirstSnapshotDoneRef.current = false;
      return;
    }
    navStartRef.current = performance.now();
    if (!import.meta.env.DEV) return;
    const navKey = `${location.pathname}${location.search}`;

    const takeSnapshot = (secondSnapshot: boolean) => {
      const renderCount = gateRenderCountRef.current;
      const flags = gateFlagsRef.current;
      setGateSnapshot({
        pathname: location.pathname,
        search: location.search,
        timestamp: Date.now(),
        renderCount,
        secondSnapshot,
        flags: {
          ...flags,
          showInitialLoader: true,
          isLoading: true,
        },
      });
      setOverlayVisible(true);
      const timeSinceNav = Math.round(performance.now() - navStartRef.current);
      console.warn(
        secondSnapshot ? '[AdminLayout] LoadingGateInspector 2nd snapshot (6s)' : '[AdminLayout] LoadingGateInspector 1st snapshot (1.5s)',
        { pathname: location.pathname, search: location.search, renderCount, timeSinceNavMs: timeSinceNav, flags }
      );
    };

    const t1 = window.setTimeout(() => {
      if (gateSnapshotNavKeyRef.current === navKey && gateFirstSnapshotDoneRef.current) return;
      gateSnapshotNavKeyRef.current = navKey;
      gateFirstSnapshotDoneRef.current = true;
      takeSnapshot(false);
    }, LOADING_OVERLAY_THRESHOLD_MS);

    const t2 = window.setTimeout(() => {
      if (!showInitialLoader) return;
      takeSnapshot(true);
    }, LOADING_SECOND_SNAPSHOT_MS);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [showInitialLoader, location.pathname, location.search, permissionsLoading, authMeLoading, permissionsSuccess, currentUser, hasAccess]);

  // Scroll to top on route change (no loading delay)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Repair-12: single transition owner — mark warm-nav transition start
  useEffect(() => {
    if (getHasSettledOnce()) {
      beginTransition(location.pathname);
    }
  }, [location.pathname]);

  // Soft decommission telemetry: legacy root alias "/" hit counter (guarded by env flag)
  useEffect(() => {
    if (location.pathname === '/') {
      incrementCompatMetric('compat.root_alias_hit', { dedupeKey: 'route:/', dedupeMs: 60_000 });
    }
  }, [location.pathname]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Handle resize - close mobile sidebar when going to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sidebarWidth = sidebarCollapsed ? 88 : 280;

  // Safety net: loading > 8s → show reload UI (no infinite spinner)
  if (showLoadErrorFallback) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-4">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-muted/30 p-6 text-center">
          <p className="text-sm font-medium text-foreground">Có lỗi khi tải. Vui lòng bấm Tải lại.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Tải lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {import.meta.env.DEV && <BootDebugOverlay />}
      {import.meta.env.DEV && <LoadingTraceOverlay />}

      <div className="min-h-screen bg-jira-surfaceSubtle">
        <AdminSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
        />

        {/* Main content */}
        <div className="relative transition-all duration-300">
          {/* Use CSS for responsive margin */}
          <style>{`
            @media (min-width: 1024px) {
              .admin-main-content {
                margin-left: ${sidebarWidth}px !important;
              }
            }
            @media (max-width: 1023px) {
              .admin-main-content {
                margin-left: 0 !important;
              }
            }
          `}</style>
          <div className="admin-main-content transition-all duration-300">
            <AdminHeader onMenuClick={() => setSidebarOpen(true)} />
            <AuthSessionCoordinatorBridge />
            <main className="p-4 lg:p-6">
                {hasAccess === false ? (
                  <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
                    <div className="p-6 bg-destructive/10 rounded-lg border border-destructive/20 max-w-md">
                      <h2 className="text-2xl font-bold text-destructive mb-2">Không có quyền</h2>
                      <p className="text-muted-foreground mb-4">
                        Bạn không có quyền truy cập trang này.
                      </p>
                      <button
                        onClick={() => {
                          const fallbackPath = getFirstAccessiblePath();
                          const resolved = resolveMenuPath(location.pathname);
                          // Only navigate if fallbackPath exists and is different from current path
                          if (fallbackPath && fallbackPath !== resolved) {
                            navigate(fallbackPath, { replace: true });
                          } else {
                            // If no accessible path or already at fallback, navigate to canonical dashboard
                            navigate('/overview', { replace: true });
                          }
                        }}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                      >
                        Về trang chủ
                      </button>
                    </div>
                  </div>
                ) : showGateLoader ? (
                  <div className="flex min-h-[400px] flex-col items-center justify-center" data-admin-gate-coordinator="true" aria-busy="true" />
                ) : hasAccess === null ? null : (
                  <Suspense fallback={<RouteChunkLoader />}>
                    <AppearanceDraftProvider>
                      <AppearanceUnsavedNavigationGuard>
                        <Outlet />
                      </AppearanceUnsavedNavigationGuard>
                    </AppearanceDraftProvider>
                  </Suspense>
                )}
            </main>
          </div>
        </div>
      </div>
    </>
  );
};
